import {
  ArrayField,
  Datagrid,
  DateField,
  EditButton,
  FunctionField,
  Labeled,
  ReferenceField,
  Show,
  SimpleShowLayout,
  TextField,
  useShowController,
} from "react-admin";
import { Grid, useMediaQuery } from "@mui/material";
import { settings } from "app";
import BackButtonToolbar from "features/BackButtonToolbar";
import ProfessorField from "server/components/Fields/ProfessorField";
import { getProfessorRecordText } from "server/components/Fields/_fieldsHelper";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import { employeeRequests } from "api/employeeApi";
import { documentRequests } from "api/documentsApi";
import { CourseChoices, GradingTypeOptions } from "utils/choices";
import DocumentFileLink from "components/Erasmus/Partials/DocumentFileLink";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { SectionTitle } from "features/SectionTitle";

const courseNames = courses =>
  courses?.map(c => CourseChoices.find(x => x.id === c?.course)?.name ?? c?.course).join(", ") ?? "-";

const ShowElectiveCourse = () => {
  const { record } = useShowController();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  return (
    <Show
      title="Детайли за свободноизбираем курс"
      actions={
        <BackButtonToolbar
          additionalButton={
            <SecuredComponent permission={permissions.canManageElectiveCourses}>
              <EditButton
                variant="contained"
                size="medium"
              />
            </SecuredComponent>
          }
        />
      }
    >
      <SimpleShowLayout>
        <SectionTitle
          title="Основна информация"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <Labeled label="Име на модула (български)">
              <TextField source="nameBg" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <Labeled label="Име на модула (английски)">
              <TextField source="nameEn" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <Labeled label="Академична година">
              <ReferenceField
                source="academicYearId"
                reference={studentRequests.getAcademicYearsSelect()}
              >
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <Labeled label="Катедра">
              <ReferenceField
                source="departmentId"
                reference={administrationRequests.getDepartmentsSelect()}
              >
                <TextField source="fullName" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <Labeled label="Ръководител катедра">
              <ReferenceField
                source="departmentLeadEmployeeId"
                reference={employeeRequests.getDepartmentLeadsSelect(record?.departmentId ?? 0)}
              >
                <FunctionField render={getProfessorRecordText} />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <Labeled label="Програма на модула">
              {record?.documentId ? (
                <DocumentFileLink
                  id={record.documentId}
                  documentName="Програма"
                  url={documentRequests.getDocumentById(record.documentId, "public")}
                />
              ) : (
                <TextField
                  source="documentId"
                  emptyText="-"
                />
              )}
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <Labeled label="Място на провеждане">
              <ReferenceField
                source="buildingId"
                reference={administrationRequests.getBuildingsSelect()}
              >
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Хорариум (лекции)">
              <TextField source="horariumHoursLectures" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Хорариум (упражнения)">
              <TextField source="horariumHoursExercises" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Кредити">
              <TextField source="credits" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Форма на оценяване">
              <FunctionField render={rec => GradingTypeOptions.find(o => o.id === rec?.gradingType)?.name ?? "-"} />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Период от">
              <DateField
                source="durationFrom"
                locales="bg-BG"
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Период до">
              <DateField
                source="durationTo"
                locales="bg-BG"
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Капацитет">
              <TextField source="studentCapacity" />
            </Labeled>
          </Grid>
        </Grid>

        <SectionTitle
          title="За кого е достъпен"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
          >
            <ArrayField
              source="electiveCourseSpecialities"
              label=""
            >
              <Datagrid
                bulkActionButtons={false}
                rowClick={false}
                sx={settings.style.detailsDatagrid}
              >
                <ReferenceField
                  source="specialityId"
                  reference={administrationRequests.getSpecialitiesSelect()}
                  label="Специалност"
                  sortable={false}
                >
                  <TextField source="name" />
                </ReferenceField>
                <FunctionField
                  label="Курсове"
                  render={rec => courseNames(rec?.courses)}
                />
              </Datagrid>
            </ArrayField>
          </Grid>
        </Grid>

        <SectionTitle
          title="Преподаватели"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
          >
            <ArrayField
              source="professors"
              label=""
            >
              <Datagrid
                bulkActionButtons={false}
                rowClick={false}
                sx={settings.style.detailsDatagrid}
              >
                <ProfessorField
                  source="professorEmployeeId"
                  label="Преподавател"
                />
              </Datagrid>
            </ArrayField>
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};

export default ShowElectiveCourse;
